import React, { useState, useEffect, useRef } from 'react';
import { API } from '../App';
import {
    Box, Stack, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TableSortLabel, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
    Chip, IconButton, Tooltip, TextField, InputAdornment, MenuItem, useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import JobModal from './JobModal';

const SORT_COLUMNS = [
    { id: 'nr', label: 'Nr.', align: 'left' },
    { id: 'title', label: 'Arbeitsanweisung', align: 'left' },
    { id: 'description', label: 'Maßnahme', align: 'left' },
    { id: 'start', label: 'Start', align: 'left' },
    { id: 'place', label: 'Ort / AKZ', align: 'left' },
    { id: 'done', label: 'Status', align: 'center' }
];

function formatNr(nr) {
    if (nr == null) return '';
    return `x${String(nr).padStart(4, '0')}`;
}

function formatStart(value) {
    if (!value) return '—';
    return new Date(value).toLocaleDateString('de-DE', {
        day: '2-digit', month: '2-digit', year: '2-digit',
        hour: '2-digit', minute: '2-digit'
    });
}

function StatusChip({ done }) {
    return done ? (
        <Chip size="small" color="success" icon={<CheckCircleIcon />} label="Erledigt" variant="outlined" />
    ) : (
        <Chip size="small" icon={<RadioButtonUncheckedIcon />} label="Offen" variant="outlined" />
    );
}

export default function JobList() {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const jobModalRef = useRef(null);
    const [jobs, setJobs] = useState([]);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize] = useState(30);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [selectedJob, setSelectedJob] = useState(null);
    const [confirmDeleteDialog, setConfirmDeleteDialog] = useState(false);
    const [query, setQuery] = useState('');
    const [orderBy, setOrderBy] = useState('nr');
    const [order, setOrder] = useState('desc');

    useEffect(() => {
        loadJobs();
        checkWritePermission();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, orderBy, order]);

    useEffect(() => {
        const POLL_MS = 15000;
        const tick = () => {
            if (document.hidden) return;
            if (jobModalRef.current?.isOpen?.()) return;
            if (confirmDeleteDialog) return;
            loadJobs();
        };
        const id = setInterval(tick, POLL_MS);
        const onVis = () => { if (!document.hidden) tick(); };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVis);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, orderBy, order, confirmDeleteDialog]);

    async function loadJobs() {
        const result = await API.getJobs({
            skip: currentPage * pageSize,
            limit: pageSize,
            sortBy: orderBy,
            sortOrder: order
        });
        if (result.success) {
            setJobs(result.msg || []);
            if (typeof result.total === 'number') setTotal(result.total);
        }
    }

    function handleSort(field) {
        if (orderBy === field) {
            setOrder(order === 'asc' ? 'desc' : 'asc');
        } else {
            setOrderBy(field);
            setOrder('asc');
        }
        setCurrentPage(0);
    }

    async function checkWritePermission() {
        const [write, admin] = await Promise.all([
            API.hasRight('write'),
            API.hasRight('admin')
        ]);
        setCanEdit(!!write?.success);
        setCanDelete(!!admin?.success);
    }

    function handleCloseModal() {
        setSelectedJob(null);
        setConfirmDeleteDialog(false);
        loadJobs();
    }

    function onRowClick(job) {
        if (canEdit && jobModalRef.current) jobModalRef.current.handleOpen(job);
    }

    function handleAddNewJob() {
        if (jobModalRef.current) jobModalRef.current.handleOpen({});
    }

    function handleEdit(job) {
        if (jobModalRef.current) jobModalRef.current.handleOpen(job);
    }

    function handleClone(job) {
        if (!jobModalRef.current) return;
        const {
            _id, nr, created, createdAt, updatedAt, __v, done,
            ...rest
        } = job;
        jobModalRef.current.handleOpen({ ...rest, done: false });
    }

    function handleDelete(job) {
        setSelectedJob(job);
        setConfirmDeleteDialog(true);
    }

    async function confirmDelete() {
        if (!selectedJob?.nr) return;
        const result = await API.deleteJob(selectedJob.nr);
        if (!result.success) alert('Fehler beim Löschen: ' + result.msg);
        handleCloseModal();
    }

    async function handleDownloadPdf(job) {
        const result = await API.downloadJobPdf(job.nr);
        if (!result.success) {
            alert('PDF konnte nicht erstellt werden: ' + (result.msg || 'Unbekannter Fehler'));
        }
    }

    const q = query.trim().toLowerCase();
    const paginated = q
        ? jobs.filter((j) =>
              [j.nr, j.title, j.description, j.place, j.contact, j.executer, j.creator]
                  .filter(Boolean)
                  .join(' ')
                  .toLowerCase()
                  .includes(q)
          )
        : jobs;
    const hasMore = (currentPage + 1) * pageSize < total;

    const rowActions = (job) => (
        <>
            <Tooltip title="PDF herunterladen">
                <IconButton size={mobile ? 'medium' : 'small'} onClick={() => handleDownloadPdf(job)}>
                    <PictureAsPdfOutlinedIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            {canEdit && (
                <Tooltip title="Als Vorlage kopieren">
                    <IconButton size={mobile ? 'medium' : 'small'} onClick={() => handleClone(job)}>
                        <ContentCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            {canEdit && (
                <Tooltip title="Bearbeiten">
                    <IconButton size={mobile ? 'medium' : 'small'} onClick={() => handleEdit(job)}>
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
            {canDelete && (
                <Tooltip title="Löschen">
                    <IconButton size={mobile ? 'medium' : 'small'} color="error" onClick={() => handleDelete(job)}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            )}
        </>
    );

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', md: 'center' }}
                justifyContent="space-between"
                sx={{ mb: 3 }}
            >
                <Typography variant="h5" sx={{ fontWeight: 600 }}>Betriebsaufträge</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ minWidth: { md: 480 } }}>
                    <TextField
                        fullWidth
                        placeholder="Suchen (Nr, Titel, Ort, Verfasser…)"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                    />
                    <Button
                        variant="contained"
                        onClick={handleAddNewJob}
                        disabled={!canEdit}
                        startIcon={<AddIcon />}
                        sx={{ flexShrink: 0 }}
                    >
                        Neuer Auftrag
                    </Button>
                </Stack>
            </Stack>

            {mobile && (
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Sortieren nach"
                        value={orderBy}
                        onChange={(e) => { setOrderBy(e.target.value); setCurrentPage(0); }}
                    >
                        {SORT_COLUMNS.map((c) => (
                            <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
                        ))}
                    </TextField>
                    <Tooltip title={order === 'asc' ? 'Aufsteigend' : 'Absteigend'}>
                        <IconButton
                            onClick={() => { setOrder(order === 'asc' ? 'desc' : 'asc'); setCurrentPage(0); }}
                            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                        >
                            {order === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                        </IconButton>
                    </Tooltip>
                </Stack>
            )}

            {mobile ? (
                <Stack spacing={1.5}>
                    {paginated.map((job) => (
                        <Paper
                            key={job._id}
                            sx={{
                                p: 2,
                                cursor: canEdit ? 'pointer' : 'default',
                                '&:active': canEdit ? { bgcolor: 'action.hover' } : {}
                            }}
                            onClick={() => onRowClick(job)}
                        >
                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                        fontWeight: 700,
                                        color: 'primary.main'
                                    }}
                                >
                                    {formatNr(job.nr)}
                                </Typography>
                                <StatusChip done={job.done} />
                            </Stack>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                                {job.title || '—'}
                            </Typography>
                            {job.description && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mt: 0.5,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {job.description}
                                </Typography>
                            )}
                            <Stack direction="row" spacing={2} sx={{ mt: 1.5, color: 'text.secondary', fontSize: 13 }}>
                                <Box><strong>Start:</strong> {formatStart(job.start)}</Box>
                                <Box><strong>Ort:</strong> {job.place || '—'}</Box>
                            </Stack>
                            <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="flex-end"
                                sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {rowActions(job)}
                            </Stack>
                        </Paper>
                    ))}
                    {paginated.length === 0 && (
                        <Paper sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">
                                {q ? 'Keine Treffer für Ihre Suche.' : 'Keine Aufträge vorhanden.'}
                            </Typography>
                        </Paper>
                    )}
                </Stack>
            ) : (
                <TableContainer component={Paper} sx={{ overflow: 'hidden' }}>
                    <Table sx={{ minWidth: 1000 }}>
                        <TableHead>
                            <TableRow>
                                {SORT_COLUMNS.map((col) => (
                                    <TableCell
                                        key={col.id}
                                        align={col.align}
                                        sortDirection={orderBy === col.id ? order : false}
                                    >
                                        <TableSortLabel
                                            active={orderBy === col.id}
                                            direction={orderBy === col.id ? order : 'asc'}
                                            onClick={() => handleSort(col.id)}
                                        >
                                            {col.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginated.map((job) => (
                                <TableRow
                                    key={job._id}
                                    hover
                                    onClick={() => onRowClick(job)}
                                    sx={{ cursor: canEdit ? 'pointer' : 'default' }}
                                >
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'primary.main' }}>
                                            {formatNr(job.nr)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {job.title || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title={job.description || ''} placement="top">
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    maxWidth: 320,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {job.description || '—'}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>{formatStart(job.start)}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                                            {job.place || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center"><StatusChip done={job.done} /></TableCell>
                                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                            {rowActions(job)}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {paginated.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                                        <Typography color="text.secondary">
                                            {q ? 'Keine Treffer für Ihre Suche.' : 'Keine Aufträge vorhanden.'}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {(currentPage > 0 || hasMore) && (
                <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 3 }}>
                    <Button
                        variant="outlined"
                        disabled={currentPage === 0}
                        onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                        startIcon={<ChevronLeftIcon />}
                        fullWidth={mobile}
                    >
                        Vorherige
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={!hasMore}
                        onClick={() => setCurrentPage((p) => p + 1)}
                        endIcon={<ChevronRightIcon />}
                        fullWidth={mobile}
                    >
                        Nächste
                    </Button>
                </Stack>
            )}

            <JobModal modalRef={jobModalRef} onSaved={loadJobs} />

            <Dialog open={confirmDeleteDialog} onClose={() => setConfirmDeleteDialog(false)}>
                <DialogTitle>Auftrag löschen?</DialogTitle>
                <DialogContent>
                    <Typography>
                        Möchten Sie Auftrag <strong>{formatNr(selectedJob?.nr)}</strong> wirklich löschen?
                        Dieser Vorgang kann nicht rückgängig gemacht werden.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDeleteDialog(false)}>Abbrechen</Button>
                    <Button onClick={confirmDelete} color="error" variant="contained">Löschen</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
